import React, { useState, useEffect, useRef } from 'react';
import { Settings, Save, Loader2, CheckCircle, Globe, Palette, Bell, Database, Image, Upload, Trash2, Eye, ToggleLeft, ToggleRight, Bot, Sparkles, Plus, GripVertical, Megaphone, Calendar, Users } from 'lucide-react';
import { adminDb } from '../lib/adminDb';
import { supabase } from '../lib/supabase';

interface AppSettings {
  appName: string;
  supportEmail: string;
  maxFreeQuestions: number;
  dailyQuestionLimit: number;
  defaultLanguage: string;
  examDurationMinutes: number;
  examQuestionCount: number;
  examPassThreshold: number;
  streakResetHours: number;
  xpCorrectAnswer: number;
  xpWrongAnswer: number;
  xpChapterComplete: number;
  xpExamPassed: number;
}

interface FeatureFlag {
  id: string;
  key: string;
  label: string;
  description: string | null;
  enabled: boolean;
}

const DEFAULT_SETTINGS: AppSettings = {
  appName: 'Fahryo',
  supportEmail: 'support@fahryo.de',
  maxFreeQuestions: 50,
  dailyQuestionLimit: 0,
  defaultLanguage: 'de',
  examDurationMinutes: 30,
  examQuestionCount: 30,
  examPassThreshold: 80,
  streakResetHours: 36,
  xpCorrectAnswer: 10,
  xpWrongAnswer: 3,
  xpChapterComplete: 50,
  xpExamPassed: 100,
};

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || '';

export const SettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<AppSettings>(DEFAULT_SETTINGS);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [featureFlags, setFeatureFlags] = useState<FeatureFlag[]>([]);
  const [flagSaving, setFlagSaving] = useState<string | null>(null);

  // Logo management state
  const [logoUrl, setLogoUrl] = useState<string>('fahryo_logo.png');
  const [logoUrlDark, setLogoUrlDark] = useState<string>('');
  const [logoHeightClient, setLogoHeightClient] = useState<string>('40');
  const [logoHeightAdmin, setLogoHeightAdmin] = useState<string>('32');
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoDarkUploading, setLogoDarkUploading] = useState(false);
  const [logoSaving, setLogoSaving] = useState(false);
  const [logoSaved, setLogoSaved] = useState(false);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string>('');
  const [logoDarkPreviewUrl, setLogoDarkPreviewUrl] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileDarkInputRef = useRef<HTMLInputElement>(null);

  // AI Coach limits
  const [aiFreeDailyLimit, setAiFreeDailyLimit] = useState('3');
  const [aiPremiumDailyLimit, setAiPremiumDailyLimit] = useState('100');
  const [aiLimitsSaving, setAiLimitsSaving] = useState(false);
  const [aiLimitsSaved, setAiLimitsSaved] = useState(false);

  // What's New Slides
  interface WhatsNewSlide {
    id: string;
    version: string;
    emoji: string;
    title: string;
    description: string;
    cta_label?: string;
    cta_navigate?: string;
    order: number;
  }
  const [whatsNewSlides, setWhatsNewSlides] = useState<WhatsNewSlide[]>([]);
  const [whatsNewSaving, setWhatsNewSaving] = useState(false);
  const [whatsNewSaved, setWhatsNewSaved] = useState(false);

  // Announcements
  interface Announcement {
    id: string;
    emoji: string;
    text: string;
    cta_label?: string;
    navigate_to?: string;
    starts_at: string;
    expires_at: string;
    target_group: string;
    active: boolean;
  }
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [announcementSaving, setAnnouncementSaving] = useState(false);

  // Fetch feature flags
  useEffect(() => {
    const fetchFlags = async () => {
      const { data } = await adminDb.from('feature_flags').select('*').order('key');
      if (data) setFeatureFlags(data);
    };
    fetchFlags();
  }, []);

  const toggleFlag = async (flag: FeatureFlag) => {
    setFlagSaving(flag.key);
    const newEnabled = !flag.enabled;
    await adminDb.from('feature_flags').update({ enabled: newEnabled, updated_at: new Date().toISOString() }).eq('key', flag.key);
    setFeatureFlags(prev => prev.map(f => f.key === flag.key ? { ...f, enabled: newEnabled } : f));
    setFlagSaving(null);
  };

  // Fetch logo settings from Supabase
  useEffect(() => {
    const fetchLogoSettings = async () => {
      try {
        const { data } = await supabase
          .from('app_settings')
          .select('key, value')
          .in('key', ['logo_url', 'logo_url_dark', 'logo_height_client', 'logo_height_admin']);
        
        if (data) {
          for (const row of data) {
            const val = typeof row.value === 'string' ? row.value : JSON.stringify(row.value);
            // Remove surrounding quotes from JSON string values
            const cleanVal = val.replace(/^"|"$/g, '');
            if (row.key === 'logo_url') setLogoUrl(cleanVal);
            if (row.key === 'logo_url_dark') setLogoUrlDark(cleanVal);
            if (row.key === 'logo_height_client') setLogoHeightClient(cleanVal);
            if (row.key === 'logo_height_admin') setLogoHeightAdmin(cleanVal);
          }
        }
      } catch (err) {
        console.error('Failed to fetch logo settings:', err);
      }
    };
    fetchLogoSettings();

    // Fetch AI limits
    const fetchAiLimits = async () => {
      try {
        const { data } = await supabase
          .from('app_settings')
          .select('key, value')
          .in('key', ['ai_free_daily_limit', 'ai_premium_daily_limit']);
        if (data) {
          for (const row of data) {
            const val = String(row.value).replace(/"/g, '');
            if (row.key === 'ai_free_daily_limit') setAiFreeDailyLimit(val);
            if (row.key === 'ai_premium_daily_limit') setAiPremiumDailyLimit(val);
          }
        }
      } catch (err) {
        console.error('Failed to fetch AI limits:', err);
      }
    };
    fetchAiLimits();

    // Fetch What's New slides
    const fetchWhatsNew = async () => {
      try {
        const { data } = await supabase
          .from('app_settings')
          .select('value')
          .eq('key', 'whats_new_slides')
          .single();
        if (data?.value) {
          const slides = Array.isArray(data.value) ? data.value : JSON.parse(data.value);
          setWhatsNewSlides(slides);
        }
      } catch (err) {
        console.error('Failed to fetch What\'s New slides:', err);
      }
    };
    fetchWhatsNew();

    // Fetch announcements
    const fetchAnnouncements = async () => {
      try {
        const session = await supabase.auth.getSession();
        const token = session.data.session?.access_token;
        if (!token) return;
        const res = await fetch('/api/announcements', {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data)) setAnnouncements(data);
        }
      } catch (err) {
        console.error('Failed to fetch announcements:', err);
      }
    };
    fetchAnnouncements();
  }, []);

  // Build the full preview URL
  useEffect(() => {
    if (logoUrl.startsWith('http')) {
      setLogoPreviewUrl(logoUrl);
    } else if (logoUrl && SUPABASE_URL) {
      // Try storage URL first, fallback to local
      const storageUrl = `${SUPABASE_URL}/storage/v1/object/public/logos/${logoUrl}`;
      setLogoPreviewUrl(storageUrl);
    } else {
      setLogoPreviewUrl(`/${logoUrl}`);
    }
  }, [logoUrl]);

  // Build dark logo preview URL
  useEffect(() => {
    if (!logoUrlDark) {
      setLogoDarkPreviewUrl('');
    } else if (logoUrlDark.startsWith('http')) {
      setLogoDarkPreviewUrl(logoUrlDark);
    } else if (logoUrlDark && SUPABASE_URL) {
      setLogoDarkPreviewUrl(`${SUPABASE_URL}/storage/v1/object/public/logos/${logoUrlDark}`);
    } else {
      setLogoDarkPreviewUrl(`/${logoUrlDark}`);
    }
  }, [logoUrlDark]);

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setLogoUploading(true);
    try {
      const ext = file.name.split('.').pop();
      const fileName = `app_logo_${Date.now()}.${ext}`;
      
      const { error: uploadError } = await adminDb.from('storage')
        .from('logos')
        .upload(fileName, file, { upsert: true });
      
      if (uploadError) throw uploadError;
      
      setLogoUrl(fileName);
    } catch (err) {
      console.error('Logo upload failed:', err);
      alert('Logo-Upload fehlgeschlagen. Bitte versuche es erneut.');
    } finally {
      setLogoUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleLogoDarkUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setLogoDarkUploading(true);
    try {
      const ext = file.name.split('.').pop();
      const fileName = `app_logo_dark_${Date.now()}.${ext}`;
      
      const { error: uploadError } = await adminDb.from('storage')
        .from('logos')
        .upload(fileName, file, { upsert: true });
      
      if (uploadError) throw uploadError;
      
      setLogoUrlDark(fileName);
    } catch (err) {
      console.error('Dark logo upload failed:', err);
      alert('Dark-Logo-Upload fehlgeschlagen. Bitte versuche es erneut.');
    } finally {
      setLogoDarkUploading(false);
      if (fileDarkInputRef.current) fileDarkInputRef.current.value = '';
    }
  };

  const handleLogoSettingsSave = async () => {
    setLogoSaving(true);
    try {
      const updates = [
        { key: 'logo_url', value: JSON.stringify(logoUrl), updated_at: new Date().toISOString() },
        { key: 'logo_url_dark', value: JSON.stringify(logoUrlDark), updated_at: new Date().toISOString() },
        { key: 'logo_height_client', value: JSON.stringify(logoHeightClient), updated_at: new Date().toISOString() },
        { key: 'logo_height_admin', value: JSON.stringify(logoHeightAdmin), updated_at: new Date().toISOString() },
      ];

      for (const item of updates) {
        await adminDb.from('app_settings').upsert(item, { onConflict: 'key' });
      }

      // Log audit
      const session = await supabase.auth.getSession();
      await adminDb.from('audit_logs').insert({
        user_id: session.data.session?.user?.id,
        action: 'settings.logo_updated',
        details: { logo_url: logoUrl, logo_height_client: logoHeightClient, logo_height_admin: logoHeightAdmin },
      });

      setLogoSaved(true);
      setTimeout(() => setLogoSaved(false), 2000);
    } catch (err) {
      console.error('Failed to save logo settings:', err);
      alert('Fehler beim Speichern der Logo-Einstellungen.');
    } finally {
      setLogoSaving(false);
    }
  };

  const handleSave = () => {
    setSaving(true);
    setTimeout(() => {
      setSaving(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    }, 500);
  };

  const update = <K extends keyof AppSettings>(key: K, value: AppSettings[K]) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div>
      <div className="page-header">
        <h1><Settings size={24} /> Einstellungen</h1>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> :
            saved ? <CheckCircle size={16} /> : <Save size={16} />}
          {saved ? 'Gespeichert!' : 'Speichern'}
        </button>
      </div>

      {/* Logo Management */}
      <div className="card mb-6">
        <h3 style={{ marginBottom: 16 }}><Image size={18} /> Logo-Verwaltung</h3>
        <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* Preview */}
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: 160,
              height: 120,
              border: '2px dashed var(--border)',
              borderRadius: 12,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--bg-secondary)',
              overflow: 'hidden',
              position: 'relative',
            }}>
              {logoPreviewUrl ? (
                <img
                  src={logoPreviewUrl}
                  alt="Logo Preview"
                  style={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain' }}
                  onError={(e) => {
                    // Fallback to local file
                    (e.target as HTMLImageElement).src = `/${logoUrl}`;
                  }}
                />
              ) : (
                <Eye size={32} style={{ opacity: 0.3 }} />
              )}
            </div>
            <span className="text-xs text-muted" style={{ marginTop: 8, display: 'block' }}>Vorschau</span>
          </div>

          {/* Controls */}
          <div style={{ flex: 1, minWidth: 250 }}>
            {/* Upload */}
            <div style={{ marginBottom: 16 }}>
              <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Logo hochladen</label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                onChange={handleLogoUpload}
                style={{ display: 'none' }}
              />
              <button
                className="btn btn-secondary"
                onClick={() => fileInputRef.current?.click()}
                disabled={logoUploading}
                style={{ display: 'flex', alignItems: 'center', gap: 8 }}
              >
                {logoUploading ? (
                  <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <Upload size={16} />
                )}
                {logoUploading ? 'Wird hochgeladen...' : 'Neues Logo wählen'}
              </button>
              <span className="text-xs text-muted" style={{ marginTop: 4, display: 'block' }}>
                PNG, JPG, SVG oder WebP (max. 5MB)
              </span>
            </div>

            {/* Dark Mode Logo Upload */}
            <div style={{ marginBottom: 16, padding: 12, background: '#1a1a2e', borderRadius: 8, border: '1px solid #333' }}>
              <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 8, color: '#e5e7eb' }}>🌙 Dark Mode Logo</label>
              
              {/* Dark Preview */}
              <div style={{
                width: '100%',
                height: 60,
                borderRadius: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#111',
                marginBottom: 8,
                overflow: 'hidden',
              }}>
                {logoDarkPreviewUrl ? (
                  <img
                    src={logoDarkPreviewUrl}
                    alt="Dark Logo"
                    style={{ maxHeight: '80%', maxWidth: '80%', objectFit: 'contain' }}
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                  />
                ) : (
                  <span className="text-xs" style={{ color: '#666' }}>Kein Dark-Logo gesetzt</span>
                )}
              </div>

              <input
                ref={fileDarkInputRef}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml,image/webp"
                onChange={handleLogoDarkUpload}
                style={{ display: 'none' }}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => fileDarkInputRef.current?.click()}
                  disabled={logoDarkUploading}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}
                >
                  {logoDarkUploading ? (
                    <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                  ) : (
                    <Upload size={14} />
                  )}
                  {logoDarkUploading ? 'Wird hochgeladen...' : 'Dark Logo wählen'}
                </button>
                {logoUrlDark && (
                  <button
                    className="btn btn-secondary"
                    onClick={() => setLogoUrlDark('')}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}
                  >
                    <Trash2 size={14} /> Entfernen
                  </button>
                )}
              </div>
              <span className="text-xs" style={{ marginTop: 4, display: 'block', color: '#9ca3af' }}>
                Wird automatisch im Dark Mode der App angezeigt
              </span>
            </div>

            {/* Size Controls */}
            <div className="grid-cols-2" style={{ gap: 16 }}>
              <div>
                <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Höhe Frontend (px)</label>
                <input
                  className="input"
                  type="number"
                  min="16"
                  max="120"
                  value={logoHeightClient}
                  onChange={e => setLogoHeightClient(e.target.value)}
                />
                <span className="text-xs text-muted">Standardmäßig 40px</span>
              </div>
              <div>
                <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Höhe Admin-Sidebar (px)</label>
                <input
                  className="input"
                  type="number"
                  min="16"
                  max="80"
                  value={logoHeightAdmin}
                  onChange={e => setLogoHeightAdmin(e.target.value)}
                />
                <span className="text-xs text-muted">Standardmäßig 32px</span>
              </div>
            </div>

            {/* Save Logo Settings */}
            <div style={{ marginTop: 16 }}>
              <button className="btn btn-primary" onClick={handleLogoSettingsSave} disabled={logoSaving}>
                {logoSaving ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> :
                  logoSaved ? <CheckCircle size={16} /> : <Save size={16} />}
                {logoSaved ? 'Logo-Einstellungen gespeichert!' : 'Logo-Einstellungen speichern'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* General */}
      <div className="card mb-6">
        <h3 style={{ marginBottom: 16 }}><Globe size={18} /> Allgemein</h3>
        <div className="grid-cols-2" style={{ gap: 16 }}>
          <div>
            <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>App-Name</label>
            <input className="input" value={settings.appName} onChange={e => update('appName', e.target.value)} />
          </div>
          <div>
            <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Support E-Mail</label>
            <input className="input" value={settings.supportEmail} onChange={e => update('supportEmail', e.target.value)} />
          </div>
          <div>
            <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Standard-Sprache</label>
            <select className="input" value={settings.defaultLanguage} onChange={e => update('defaultLanguage', e.target.value)}>
              <option value="de">Deutsch</option>
              <option value="en">Englisch</option>
              <option value="tr">Tuerkisch</option>
              <option value="ar">Arabisch</option>
            </select>
          </div>
          <div>
            <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Max. Free Fragen</label>
            <input className="input" type="number" value={settings.maxFreeQuestions} onChange={e => update('maxFreeQuestions', parseInt(e.target.value))} />
            <span className="text-xs text-muted">0 = unbegrenzt</span>
          </div>
        </div>
      </div>

      {/* Exam Settings */}
      <div className="card mb-6">
        <h3 style={{ marginBottom: 16 }}><Database size={18} /> Prüfungssimulation</h3>
        <div className="grid-cols-3" style={{ gap: 16 }}>
          <div>
            <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Dauer (Minuten)</label>
            <input className="input" type="number" value={settings.examDurationMinutes} onChange={e => update('examDurationMinutes', parseInt(e.target.value))} />
          </div>
          <div>
            <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Fragenanzahl</label>
            <input className="input" type="number" value={settings.examQuestionCount} onChange={e => update('examQuestionCount', parseInt(e.target.value))} />
          </div>
          <div>
            <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Bestehensschwelle (%)</label>
            <input className="input" type="number" value={settings.examPassThreshold} onChange={e => update('examPassThreshold', parseInt(e.target.value))} />
          </div>
        </div>
      </div>

      {/* Gamification */}
      <div className="card mb-6">
        <h3 style={{ marginBottom: 16 }}><Palette size={18} /> Gamification</h3>
        <div className="grid-cols-2" style={{ gap: 16 }}>
          <div>
            <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>XP pro korrekte Antwort</label>
            <input className="input" type="number" value={settings.xpCorrectAnswer} onChange={e => update('xpCorrectAnswer', parseInt(e.target.value))} />
          </div>
          <div>
            <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>XP pro falsche Antwort</label>
            <input className="input" type="number" value={settings.xpWrongAnswer} onChange={e => update('xpWrongAnswer', parseInt(e.target.value))} />
          </div>
          <div>
            <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>XP Kapitel abgeschlossen</label>
            <input className="input" type="number" value={settings.xpChapterComplete} onChange={e => update('xpChapterComplete', parseInt(e.target.value))} />
          </div>
          <div>
            <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>XP Prüfung bestanden</label>
            <input className="input" type="number" value={settings.xpExamPassed} onChange={e => update('xpExamPassed', parseInt(e.target.value))} />
          </div>
          <div>
            <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Streak Reset (Stunden)</label>
            <input className="input" type="number" value={settings.streakResetHours} onChange={e => update('streakResetHours', parseInt(e.target.value))} />
            <span className="text-xs text-muted">Nach dieser Zeit wird der Streak zurückgesetzt</span>
          </div>
        </div>
      </div>

      {/* AI Coach Limits */}
      <div className="card mb-6">
        <h3 style={{ marginBottom: 16 }}><Bot size={18} /> KI-Coach Limits</h3>
        <p className="text-xs text-muted" style={{ marginBottom: 12 }}>Konfiguriere die maximale Anzahl an KI-Nachrichten pro Tag für Free- und Premium-Nutzer.</p>
        <div className="grid-cols-2" style={{ gap: 16 }}>
          <div>
            <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Free-Nutzer (Nachrichten/Tag)</label>
            <input className="input" type="number" min="0" max="100" value={aiFreeDailyLimit} onChange={e => setAiFreeDailyLimit(e.target.value)} />
            <span className="text-xs text-muted">Empfohlen: 3 (0 = deaktiviert)</span>
          </div>
          <div>
            <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Premium-Nutzer (Nachrichten/Tag)</label>
            <input className="input" type="number" min="0" max="1000" value={aiPremiumDailyLimit} onChange={e => setAiPremiumDailyLimit(e.target.value)} />
            <span className="text-xs text-muted">Empfohlen: 100</span>
          </div>
        </div>
        <div style={{ marginTop: 12 }}>
          <button className="btn btn-primary" disabled={aiLimitsSaving} onClick={async () => {
            setAiLimitsSaving(true);
            try {
              await adminDb.from('app_settings').upsert({ key: 'ai_free_daily_limit', value: aiFreeDailyLimit, updated_at: new Date().toISOString() }, { onConflict: 'key' });
              await adminDb.from('app_settings').upsert({ key: 'ai_premium_daily_limit', value: aiPremiumDailyLimit, updated_at: new Date().toISOString() }, { onConflict: 'key' });
              setAiLimitsSaved(true);
              setTimeout(() => setAiLimitsSaved(false), 2000);
            } catch (err) {
              console.error('Failed to save AI limits:', err);
              alert('Fehler beim Speichern der KI-Limits.');
            } finally {
              setAiLimitsSaving(false);
            }
          }}>
            {aiLimitsSaving ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> :
              aiLimitsSaved ? <CheckCircle size={16} /> : <Save size={16} />}
            {aiLimitsSaved ? 'KI-Limits gespeichert!' : 'KI-Limits speichern'}
          </button>
        </div>
      </div>

      {/* What's New Slides */}
      <div className="card mb-6">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3><Sparkles size={18} /> What's New Slides</h3>
          <button className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }} onClick={() => {
            const pkg = { version: '1.80.0' }; // Default version
            setWhatsNewSlides(prev => [...prev, {
              id: `slide_${Date.now()}`,
              version: pkg.version,
              emoji: '🎉',
              title: 'Neues Feature',
              description: 'Beschreibung des neuen Features.',
              order: prev.length + 1,
            }]);
          }}>
            <Plus size={14} /> Slide hinzufügen
          </button>
        </div>
        <p className="text-xs text-muted" style={{ marginBottom: 12 }}>Konfiguriere die Slides, die Nutzern nach einem App-Update angezeigt werden. Slides werden nach Version gefiltert.</p>

        {whatsNewSlides.length === 0 && (
          <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)', fontSize: 14 }}>
            Keine Slides konfiguriert. Füge einen hinzu, um Nutzer über neue Features zu informieren.
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {whatsNewSlides.map((slide, idx) => (
            <div key={slide.id} style={{
              padding: 16, borderRadius: 12,
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border)',
              position: 'relative',
            }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                {/* Emoji + Order */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 50 }}>
                  <input
                    className="input"
                    value={slide.emoji}
                    onChange={e => {
                      const updated = [...whatsNewSlides];
                      updated[idx] = { ...updated[idx], emoji: e.target.value };
                      setWhatsNewSlides(updated);
                    }}
                    style={{ textAlign: 'center', fontSize: 24, width: 50, padding: '4px 0' }}
                    title="Emoji"
                  />
                  <span className="text-xs text-muted">#{slide.order}</span>
                </div>

                {/* Fields */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div className="grid-cols-2" style={{ gap: 8 }}>
                    <div>
                      <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 2 }}>Version</label>
                      <input className="input" value={slide.version} onChange={e => {
                        const updated = [...whatsNewSlides];
                        updated[idx] = { ...updated[idx], version: e.target.value };
                        setWhatsNewSlides(updated);
                      }} placeholder="z.B. 1.80.0" />
                    </div>
                    <div>
                      <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 2 }}>Titel</label>
                      <input className="input" value={slide.title} onChange={e => {
                        const updated = [...whatsNewSlides];
                        updated[idx] = { ...updated[idx], title: e.target.value };
                        setWhatsNewSlides(updated);
                      }} placeholder="Feature-Titel" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 2 }}>Beschreibung</label>
                    <textarea className="input" value={slide.description} onChange={e => {
                      const updated = [...whatsNewSlides];
                      updated[idx] = { ...updated[idx], description: e.target.value };
                      setWhatsNewSlides(updated);
                    }} rows={2} style={{ resize: 'vertical' }} placeholder="Was ist neu?" />
                  </div>
                  <div className="grid-cols-2" style={{ gap: 8 }}>
                    <div>
                      <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 2 }}>CTA-Button (optional)</label>
                      <input className="input" value={slide.cta_label || ''} onChange={e => {
                        const updated = [...whatsNewSlides];
                        updated[idx] = { ...updated[idx], cta_label: e.target.value };
                        setWhatsNewSlides(updated);
                      }} placeholder="z.B. Jetzt testen" />
                    </div>
                    <div>
                      <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 2 }}>Navigation (Tab)</label>
                      <select className="input" value={slide.cta_navigate || ''} onChange={e => {
                        const updated = [...whatsNewSlides];
                        updated[idx] = { ...updated[idx], cta_navigate: e.target.value };
                        setWhatsNewSlides(updated);
                      }}>
                        <option value="">— Keine —</option>
                        <option value="learn">Lernen</option>
                        <option value="exam">Prüfung</option>
                        <option value="leaderboard">Rangliste</option>
                        <option value="chat">KI-Coach</option>
                        <option value="profile">Profil</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Delete */}
                <button
                  onClick={() => setWhatsNewSlides(prev => prev.filter((_, i) => i !== idx))}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8, color: '#ef4444' }}
                  title="Slide entfernen"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>

        {whatsNewSlides.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <button className="btn btn-primary" disabled={whatsNewSaving} onClick={async () => {
              setWhatsNewSaving(true);
              try {
                await adminDb.from('app_settings').upsert({
                  key: 'whats_new_slides',
                  value: whatsNewSlides,
                  updated_at: new Date().toISOString(),
                }, { onConflict: 'key' });
                setWhatsNewSaved(true);
                setTimeout(() => setWhatsNewSaved(false), 2000);
              } catch (err) {
                console.error('Failed to save What\'s New slides:', err);
                alert('Fehler beim Speichern der Slides.');
              } finally {
                setWhatsNewSaving(false);
              }
            }}>
              {whatsNewSaving ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> :
                whatsNewSaved ? <CheckCircle size={16} /> : <Save size={16} />}
              {whatsNewSaved ? 'Slides gespeichert!' : 'Slides speichern'}
            </button>
          </div>
        )}
      </div>

      {/* In-App Announcements */}
      <div className="card mb-6">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3><Megaphone size={18} /> In-App Ankündigungen</h3>
          <button className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }} onClick={async () => {
            setAnnouncementSaving(true);
            try {
              const session = await supabase.auth.getSession();
              const token = session.data.session?.access_token;
              const res = await fetch('/api/announcements', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                  emoji: '🎉',
                  text: 'Neue Ankündigung',
                  target_group: 'all',
                  starts_at: new Date().toISOString(),
                  expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
                }),
              });
              if (res.ok) {
                const newAnn = await res.json();
                setAnnouncements(prev => [newAnn, ...prev]);
              }
            } catch (err) {
              console.error('Failed to create announcement:', err);
            } finally {
              setAnnouncementSaving(false);
            }
          }}>
            {announcementSaving ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Plus size={14} />}
            Ankündigung erstellen
          </button>
        </div>
        <p className="text-xs text-muted" style={{ marginBottom: 12 }}>
          Erstelle zeitlich begrenzte Banner, die allen oder bestimmten Nutzergruppen oben in der App angezeigt werden.
        </p>

        {announcements.length === 0 && (
          <div style={{ textAlign: 'center', padding: 24, color: 'var(--text-muted)', fontSize: 14 }}>
            Keine Ankündigungen vorhanden.
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {announcements.map((ann) => {
            const now = new Date();
            const isExpired = new Date(ann.expires_at) < now;
            const isScheduled = new Date(ann.starts_at) > now;
            const isLive = ann.active && !isExpired && !isScheduled;
            const statusColor = isLive ? '#22c55e' : isScheduled ? '#f59e0b' : '#9ca3af';
            const statusLabel = isLive ? 'Live' : isScheduled ? 'Geplant' : isExpired ? 'Abgelaufen' : 'Inaktiv';

            return (
              <div key={ann.id} style={{
                padding: 16, borderRadius: 12,
                background: 'var(--bg-secondary)',
                border: `1px solid ${isLive ? '#22c55e40' : 'var(--border)'}`,
                opacity: isExpired ? 0.6 : 1,
              }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  {/* Emoji + Status */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 50 }}>
                    <input
                      className="input"
                      value={ann.emoji}
                      onChange={e => setAnnouncements(prev => prev.map(a => a.id === ann.id ? { ...a, emoji: e.target.value } : a))}
                      style={{ textAlign: 'center', fontSize: 24, width: 50, padding: '4px 0' }}
                    />
                    <span style={{ fontSize: 10, fontWeight: 700, color: statusColor, textTransform: 'uppercase' }}>
                      ● {statusLabel}
                    </span>
                  </div>

                  {/* Fields */}
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div>
                      <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 2 }}>Text</label>
                      <input className="input" value={ann.text} onChange={e => setAnnouncements(prev => prev.map(a => a.id === ann.id ? { ...a, text: e.target.value } : a))} placeholder="Ankündigungstext" />
                    </div>
                    <div className="grid-cols-2" style={{ gap: 8 }}>
                      <div>
                        <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 2 }}>CTA (optional)</label>
                        <input className="input" value={ann.cta_label || ''} onChange={e => setAnnouncements(prev => prev.map(a => a.id === ann.id ? { ...a, cta_label: e.target.value } : a))} placeholder="z.B. Jetzt testen →" />
                      </div>
                      <div>
                        <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 2 }}>Navigation</label>
                        <select className="input" value={ann.navigate_to || ''} onChange={e => setAnnouncements(prev => prev.map(a => a.id === ann.id ? { ...a, navigate_to: e.target.value } : a))}>
                          <option value="">— Keine —</option>
                          <option value="learn">Lernen</option>
                          <option value="exam">Prüfung</option>
                          <option value="leaderboard">Rangliste</option>
                          <option value="chat">KI-Coach</option>
                          <option value="profile">Profil</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid-cols-3" style={{ gap: 8 }}>
                      <div>
                        <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 2 }}><Calendar size={10} style={{ display: 'inline' }} /> Start</label>
                        <input className="input" type="datetime-local" value={ann.starts_at?.slice(0, 16)} onChange={e => setAnnouncements(prev => prev.map(a => a.id === ann.id ? { ...a, starts_at: new Date(e.target.value).toISOString() } : a))} />
                      </div>
                      <div>
                        <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 2 }}><Calendar size={10} style={{ display: 'inline' }} /> Ende</label>
                        <input className="input" type="datetime-local" value={ann.expires_at?.slice(0, 16)} onChange={e => setAnnouncements(prev => prev.map(a => a.id === ann.id ? { ...a, expires_at: new Date(e.target.value).toISOString() } : a))} />
                      </div>
                      <div>
                        <label className="text-xs" style={{ fontWeight: 600, display: 'block', marginBottom: 2 }}><Users size={10} style={{ display: 'inline' }} /> Zielgruppe</label>
                        <select className="input" value={ann.target_group} onChange={e => setAnnouncements(prev => prev.map(a => a.id === ann.id ? { ...a, target_group: e.target.value } : a))}>
                          <option value="all">Alle</option>
                          <option value="free">Free</option>
                          <option value="premium">Premium</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <button
                      onClick={async () => {
                        const session = await supabase.auth.getSession();
                        const token = session.data.session?.access_token;
                        await fetch(`/api/announcements/${ann.id}`, {
                          method: 'PUT',
                          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                          body: JSON.stringify(ann),
                        });
                        alert('✅ Gespeichert!');
                      }}
                      style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 8, padding: 6, cursor: 'pointer', color: '#22c55e' }}
                      title="Speichern"
                    >
                      <Save size={14} />
                    </button>
                    <button
                      onClick={async () => {
                        if (!confirm('Ankündigung wirklich löschen?')) return;
                        const session = await supabase.auth.getSession();
                        const token = session.data.session?.access_token;
                        await fetch(`/api/announcements/${ann.id}`, {
                          method: 'DELETE',
                          headers: { 'Authorization': `Bearer ${token}` },
                        });
                        setAnnouncements(prev => prev.filter(a => a.id !== ann.id));
                      }}
                      style={{ background: 'none', border: 'none', padding: 6, cursor: 'pointer', color: '#ef4444' }}
                      title="Löschen"
                    >
                      <Trash2 size={14} />
                    </button>
                    <button
                      onClick={async () => {
                        const session = await supabase.auth.getSession();
                        const token = session.data.session?.access_token;
                        const newActive = !ann.active;
                        await fetch(`/api/announcements/${ann.id}`, {
                          method: 'PUT',
                          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                          body: JSON.stringify({ active: newActive }),
                        });
                        setAnnouncements(prev => prev.map(a => a.id === ann.id ? { ...a, active: newActive } : a));
                      }}
                      style={{ background: 'none', border: 'none', padding: 6, cursor: 'pointer', color: ann.active ? '#22c55e' : 'var(--text-muted)' }}
                      title={ann.active ? 'Deaktivieren' : 'Aktivieren'}
                    >
                      {ann.active ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Feature Flags */}
      {featureFlags.length > 0 && (
        <div className="card mb-6">
          <h3 style={{ marginBottom: 16 }}><Bell size={18} /> Feature-Flags</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {featureFlags.map(flag => (
              <div key={flag.key} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '12px 16px', borderRadius: 8, background: 'var(--bg-secondary)', border: '1px solid var(--border)'
              }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{flag.label}</div>
                  {flag.description && <div className="text-xs text-muted" style={{ marginTop: 2 }}>{flag.description}</div>}
                </div>
                <button
                  onClick={() => toggleFlag(flag)}
                  disabled={flagSaving === flag.key}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, transition: 'transform 0.2s' }}
                  title={flag.enabled ? 'Deaktivieren' : 'Aktivieren'}
                >
                  {flagSaving === flag.key ? (
                    <Loader2 size={28} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-muted)' }} />
                  ) : flag.enabled ? (
                    <ToggleRight size={28} style={{ color: '#22c55e' }} />
                  ) : (
                    <ToggleLeft size={28} style={{ color: 'var(--text-muted)' }} />
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};
